import { useCallback, useEffect, useMemo, useState } from "react"
import { PayloadAction } from "@reduxjs/toolkit"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import {
  fetchDonationHistory,
  fetchOrdersHistory,
  orderProduct,
  RootState,
  selectHasAccess,
  selectHasLoadedAccountData,
  selectHasLoadedWalletBalances,
  selectIsMarketTransactionsHistoryFetching,
  selectMarketProductByTransactionHash,
  selectOrdersHistory,
  selectWalletAddress,
  selectWalletBalances,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  MARKET_GIFT_CARD_ITEMS,
  MARKET_STORE_PRODUCT_ITEMS,
  ONE_MINUTE_IN_SECONDS,
  ONE_SECOND_IN_MILLISECONDS,
  queryKeysFactory,
} from "#/shared/constants"

import { useMezoAssetsBalance } from "./assets"
import { useDonationDataByBeneficiaryId } from "./donation"
import { usePosthogMarketTransactionEvent } from "./posthog-events"

export const useMarketData = () => {
  const address = useDappSelector(selectWalletAddress)
  const hasAccess = useDappSelector(selectHasAccess)

  const [lastFetchedAddress, setLastFetchedAddress] = useState<string | null>(
    null,
  )

  const hasLoadedAccountData = useDappSelector(selectHasLoadedAccountData)

  const dispatch = useDappDispatch()

  /**
   * Fetches market history for currently connected user.
   * Does not fetch anything if user is not connected and signed with SIWE.
   */
  const fetchMarketHistory = useCallback(async () => {
    if (!address || !hasLoadedAccountData || !hasAccess) {
      return
    }

    if (address !== lastFetchedAddress) {
      await dispatch(fetchDonationHistory())
      await dispatch(fetchOrdersHistory())
      setLastFetchedAddress(address)
    }
  }, [address, lastFetchedAddress, dispatch, hasAccess, hasLoadedAccountData])

  useEffect(() => {
    const fetchMarketData = async () => {
      await fetchMarketHistory()
    }

    fetchMarketData()
  }, [dispatch, fetchMarketHistory])
}

export const useOrderProduct = (chainProductId: number | undefined) => {
  const dispatch = useDappDispatch()
  const queryClient = useQueryClient()

  const { data: productDetails } =
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useMarketStoreProductDetailsById(chainProductId)

  const [transactionHash, setTransactionHash] = useState<string>()

  const { MUSD } = useDappSelector(selectWalletBalances)
  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)

  const { fetchMezoAssetsBalance } = useMezoAssetsBalance()

  const [redeemCode, setRedeemCode] = useState<string>()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const [hasOrderProductHandlerError, setHasOrderProductHandlerError] =
    useState(false)

  const [orderProductHandlerErrorMessage, setOrderProductHandlerErrorMessage] =
    useState("")

  const { emitTransactionSuccessEvent, emitTransactionErrorEvent } =
    usePosthogMarketTransactionEvent()

  const orderProductHandler = useCallback(async () => {
    setIsProcessing(true)
    if (!chainProductId) {
      setHasOrderProductHandlerError(true)
      setOrderProductHandlerErrorMessage(
        "Product data did not fetch properly. Please try again.",
      )
      setIsProcessing(false)
      return
    }
    setHasOrderProductHandlerError(false)
    setOrderProductHandlerErrorMessage("")

    const result = (await dispatch(
      orderProduct({
        productId: chainProductId,
      }),
    )) as PayloadAction<{ transactionHash: string; redemptionCode: string }>

    const { payload } = result

    if (payload && payload.transactionHash && payload.redemptionCode) {
      setTransactionHash(payload.transactionHash)
      setRedeemCode(payload.redemptionCode)
      await queryClient.invalidateQueries({
        queryKey: queryKeysFactory.mezoKeys.productDetails(),
        refetchType: "all",
      })
      setIsSuccess(true)
      emitTransactionSuccessEvent("purchase", productDetails?.price)

      await fetchMezoAssetsBalance(["MUSD", "mBTC"])
    } else if (payload && payload.transactionHash && !payload.redemptionCode) {
      setIsSuccess(false)
      setHasOrderProductHandlerError(true)
      setOrderProductHandlerErrorMessage("Failed to claim the code.")
      emitTransactionErrorEvent()

      await fetchMezoAssetsBalance(["MUSD", "mBTC"])
    } else {
      setIsSuccess(false)
      setHasOrderProductHandlerError(true)
      setOrderProductHandlerErrorMessage("Transaction rejected.")
      emitTransactionErrorEvent()
    }
    setIsProcessing(false)
  }, [
    chainProductId,
    dispatch,
    queryClient,
    emitTransactionSuccessEvent,
    productDetails,
    emitTransactionErrorEvent,
    fetchMezoAssetsBalance,
  ])

  const hasEnoughFunds = useMemo(
    () =>
      hasLoadedWalletBalances && productDetails?.price
        ? MUSD.balance >= BigInt(productDetails.price)
        : true,
    [MUSD.balance, productDetails?.price, hasLoadedWalletBalances],
  )

  const hasError = useMemo(
    () => !hasEnoughFunds || hasOrderProductHandlerError,
    [hasEnoughFunds, hasOrderProductHandlerError],
  )

  const errorMessage = useMemo(
    () =>
      !hasEnoughFunds ? "Not enough funds." : orderProductHandlerErrorMessage,
    [hasEnoughFunds, orderProductHandlerErrorMessage],
  )

  return {
    orderProduct: orderProductHandler,
    isProcessing,
    isSuccess,
    hasEnoughFunds,
    hasError,
    errorMessage,
    result: {
      transactionHash,
      redeemCode,
    },
  }
}

export const useMarketStoreProductDataById = (
  productId: string | null | undefined,
) =>
  useMemo(
    () =>
      productId
        ? MARKET_STORE_PRODUCT_ITEMS.find(
            (marketStoreProductItem) => marketStoreProductItem.id === productId,
          )
        : undefined,
    [productId],
  )

export const useMarketGiftCardDataById = (
  giftCardId: string | null | undefined,
) =>
  useMemo(
    () =>
      giftCardId
        ? MARKET_GIFT_CARD_ITEMS.find(
            (marketGiftCardItem) => marketGiftCardItem.id === giftCardId,
          )
        : undefined,
    [giftCardId],
  )

const useMarketStoreProductDataByChainProductId = (
  productId: string | null | undefined,
) =>
  useMemo(
    () =>
      productId
        ? MARKET_STORE_PRODUCT_ITEMS.find(
            (marketStoreProductItem) =>
              marketStoreProductItem.chainProductId === Number(productId),
          )
        : undefined,
    [productId],
  )

const useMarketGiftCardDataByChainProductId = (
  giftCardId: string | null | undefined,
) =>
  useMemo(
    () =>
      giftCardId
        ? MARKET_GIFT_CARD_ITEMS.find((marketGiftCardItem) =>
            marketGiftCardItem.chainProductId.includes(Number(giftCardId)),
          )
        : undefined,
    [giftCardId],
  )

export const useMarketProductDataByTransactionHash = (
  transactionHash: string,
) => {
  const marketProduct = useDappSelector((state: RootState) =>
    selectMarketProductByTransactionHash(state, transactionHash),
  )

  const donationData = useDonationDataByBeneficiaryId(
    marketProduct && marketProduct.type === "donation"
      ? marketProduct.beneficiaryId.toString()
      : undefined,
  )

  const giftCardData = useMarketGiftCardDataByChainProductId(
    marketProduct && marketProduct.type === "order"
      ? marketProduct.productId.toString()
      : undefined,
  )

  const storeProductData = useMarketStoreProductDataByChainProductId(
    marketProduct && marketProduct.type === "order"
      ? marketProduct.productId.toString()
      : undefined,
  )

  const productData = useMemo(() => {
    if (donationData) return donationData
    if (giftCardData) return giftCardData
    return storeProductData
  }, [donationData, giftCardData, storeProductData])

  return productData
}

type ProductDetails = {
  id: number
  price: string
  stock: number
  perCustomerLimit: number
}

const refetchInterval = ONE_MINUTE_IN_SECONDS * 5 * ONE_SECOND_IN_MILLISECONDS
const staleTime = refetchInterval

const useProductsDetails = () => {
  const url = new URL("store/products", process.env.API_ENDPOINT_V2)

  return useQuery({
    queryKey: queryKeysFactory.mezoKeys.productDetails(),
    queryFn: async () => {
      const response = await fetch(url, {
        credentials: "include",
      })

      if (!response.ok) {
        throw new Error(`Could not get product details: ${response.status}`)
      }

      const responseData = (await response.json()) as ProductDetails[]

      return responseData
    },
    refetchInterval,
    staleTime,
  })
}

export const useMarketStoreProductDetailsById = (
  chainProductId: number | undefined,
) => {
  const { data, ...restProps } = useProductsDetails()

  if (!chainProductId || !data) return { data: undefined, ...restProps }

  return {
    data: data.find((productDetails) => productDetails.id === chainProductId),
    ...restProps,
  }
}

export const useMarketStoreProductDetailsByIds = (
  chainProductIds: number[] | undefined,
) => {
  const { data: productsDetails, ...restProps } = useProductsDetails()

  if (!chainProductIds || !productsDetails)
    return { data: undefined, ...restProps }

  return {
    data: productsDetails
      .filter((productDetails) => chainProductIds.includes(productDetails.id))
      // Sort by price ascending
      .sort((a, b) => {
        const diff = BigInt(a.price) - BigInt(b.price)
        if (diff > 0) return 1
        if (diff < 0) return -1
        return 0
      }),
    ...restProps,
  }
}

export const useMarketStoreProductAvailability = (
  chainProductId: number | undefined,
) => {
  const isMarketTransactionHistoryFetching = useDappSelector(
    selectIsMarketTransactionsHistoryFetching,
  )

  const {
    data: storeProductDetails,
    isPending: isStoreProductPending,
    isError: isStoreProductError,
  } = useMarketStoreProductDetailsById(chainProductId)

  const ordersHistory = useDappSelector(selectOrdersHistory)

  const isInStock = useMemo(
    () => storeProductDetails?.stock && storeProductDetails.stock > 0,
    [storeProductDetails?.stock],
  )

  const isLimitPerCustomerReached = useMemo(() => {
    if (
      !storeProductDetails ||
      !storeProductDetails.perCustomerLimit ||
      storeProductDetails.perCustomerLimit === 0
    )
      return false

    const numberOfProductsInHistory = ordersHistory.filter(
      (order) => order.productId === chainProductId,
    ).length

    if (numberOfProductsInHistory < storeProductDetails.perCustomerLimit)
      return false

    return true
  }, [storeProductDetails, ordersHistory, chainProductId])

  const isFetching = useMemo(() => {
    if (isStoreProductPending || isMarketTransactionHistoryFetching) return true
    return false
  }, [isStoreProductPending, isMarketTransactionHistoryFetching])

  const submitButtonText = useMemo(() => {
    if (chainProductId) {
      if (!isFetching && isStoreProductError) return "Couldn't fetch data."
      if (!storeProductDetails && !isFetching && !isStoreProductError)
        return "Product not available"
      if (isFetching) return "Fetching data..."
      if (isLimitPerCustomerReached)
        return `Already Purchased! (Limit ${storeProductDetails?.perCustomerLimit})`
      if (!isInStock) return "Sold Out!"
    }
    return "Buy with MUSD"
  }, [
    isInStock,
    isLimitPerCustomerReached,
    storeProductDetails,
    chainProductId,
    isFetching,
    isStoreProductError,
  ])

  return {
    isFetching,
    isInStock,
    isLimitPerCustomerReached,
    perCustomerLimit: storeProductDetails?.perCustomerLimit,
    submitButtonText,
  }
}
